import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import ScheduleBoard from "../components/ScheduleBoard";
import DispatchPanel from "../components/DispatchPanel";
import DispatchPoolPanel from "../components/DispatchPoolPanel";
import DispatchLinear from "../components/DispatchLinear";
import ScheduleCalendar from "../components/ScheduleCalendar";
import Segmented from "@/components/Segmented";
import EmailDispatchDialog from "../components/EmailDispatchDialog";
import { Button } from "@/components/ui/button";
import { Zap, X, AlertTriangle, Check, Users, Wrench, Undo2, Mail } from "lucide-react";
import { toast } from "sonner";

/**
 * Dispatch page: installer × date grid.
 * Pool shows mobs with status=ready_to_dispatch (or scheduled, auto-flipped at 14d).
 * "Auto Dispatch" button runs the assignment logic in PREVIEW mode first,
 * opens a modal, then commits on user confirm.
 */
export default function DispatchPage() {
  const [showPanel, setShowPanel] = useState(false);
  // DSP-1 — view + grouping + expanded groups are remembered per user (server-side `user_prefs`).
  const [prefs, setPrefs] = useState({ dispatch_view: "board", dispatch_grouping: "crew", expanded: {}, can_write: false });
  useEffect(() => { api.get("/dispatch/prefs").then((r) => setPrefs(r.data)).catch(() => {}); }, []);
  const savePrefs = (patch) => { setPrefs((c) => ({ ...c, ...patch })); api.put("/dispatch/prefs", patch).catch(() => {}); };
  const [running, setRunning] = useState(false);
  const [preview, setPreview] = useState(null);
  const [committing, setCommitting] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);
  // Snapshot of the most recent commit so the user can undo it. Cleared
  // after a successful undo or if a fresh auto-dispatch overwrites it.
  const [lastRun, setLastRun] = useState(null);
  const [undoing, setUndoing] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  // Foreman quick requests: polled every minute so a new one shows on the header badge.
  const [requests, setRequests] = useState(0);
  const [poolKey, setPoolKey] = useState(0);
  useEffect(() => {
    let last = -1;
    const poll = () => api.get("/dispatch/quick-requests").then((r) => {
      const n = (r.data || []).length; setRequests(n);
      if (last !== -1 && n !== last) setPoolKey((k) => k + 1);
      last = n;
    }).catch(() => {});
    poll(); const t = setInterval(poll, 60000); return () => clearInterval(t);
  }, []);
  const openRequests = () => {
    savePrefs({ dispatch_view: "board", expanded: { ...(prefs.expanded || {}), quick: true } });
    setPoolKey((k) => k + 1);
    setTimeout(() => document.getElementById("dispatch-pool")?.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
  };
  // FLD-6 — rooms on this run whose material is not on hand / not ordered.
  // The scheduler is warned inline and may still proceed; proceeding is logged.
  const [gaps, setGaps] = useState([]);

  const openPreview = async () => {
    setRunning(true);
    try {
      const r = await api.post("/dispatch/auto", { preview: true });
      setPreview(r.data);
      const ids = (r.data?.plan || []).map((p) => p.mobilization_id).filter(Boolean);
      if (ids.length) {
        try {
          const w = await api.post("/material/status/dispatch-check", { mobilization_ids: ids });
          setGaps(w.data?.warnings || []);
        } catch { setGaps([]); }
      } else { setGaps([]); }
    } catch {
      toast.error("Auto-dispatch preview failed");
    } finally {
      setRunning(false);
    }
  };

  const commit = async () => {
    setCommitting(true);
    try {
      const r = await api.post("/dispatch/auto", { preview: false });
      toast.success(`Dispatched ${r.data.dispatched} mobs · ${r.data.created} assignments`);
      if (gaps.length) {
        await api.post("/material/status/dispatch-proceed", {
          mobilization_ids: gaps.map((g) => g.mobilization_id),
          note: "Auto Dispatch committed with material still short",
        }).catch(() => {});
      }
      // Stash this run's run_id + prior mob states so the user can undo.
      if (r.data.run_id) {
        setLastRun({ run_id: r.data.run_id, prior_mob_states: r.data.prior_mob_states || [] });
      }
      setPreview(null);
      setGaps([]);
      setRefreshToken((t) => t + 1);
    } catch {
      toast.error("Auto-dispatch commit failed");
    } finally {
      setCommitting(false);
    }
  };

  const undo = async () => {
    if (!lastRun?.run_id) return;
    setUndoing(true);
    try {
      const r = await api.post("/dispatch/auto/undo", lastRun);
      toast.success(`Undone — ${r.data.deleted_assignments} assignments cleared, ${r.data.restored_mobs} mobs restored`);
      setLastRun(null);
      setRefreshToken((t) => t + 1);
    } catch {
      toast.error("Undo failed");
    } finally {
      setUndoing(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          {requests > 0 && (
            <button type="button" onClick={openRequests} data-testid="dispatch-requests-badge"
                    title="Foreman quick requests waiting for dispatch"
                    className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-800 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:text-slate-100 dark:hover:bg-slate-800">
              <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-60" /><span className="relative inline-flex h-2 w-2 rounded-full bg-red-600" /></span>
              Requests {requests}
            </button>
          )}
          <Button
            onClick={openPreview}
            disabled={running}
            variant="outline"
            className="border border-emerald-600 bg-transparent text-emerald-700 hover:bg-emerald-50 dark:border-emerald-500 dark:text-emerald-400 dark:hover:bg-emerald-950/40"
            data-testid="dispatch-auto-btn"
          >
            <Zap className="w-3.5 h-3.5 mr-1" /> {running ? "Planning…" : "Auto Dispatch"}
          </Button>
          {lastRun?.run_id && (
            <Button
              onClick={undo}
              disabled={undoing}
              variant="outline"
              className="border-slate-300 text-slate-800 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-100 dark:hover:bg-slate-800"
              data-testid="dispatch-undo-btn"
              title="Undo the last Auto Dispatch run — clears only assignments created by that run; manual placements remain."
            >
              <Undo2 className="w-3.5 h-3.5 mr-1" /> {undoing ? "Undoing…" : "Undo Auto Dispatch"}
            </Button>
          )}
          <Button variant="outline" className="border-slate-300 text-slate-800 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-100 dark:hover:bg-slate-800" onClick={() => setShowPanel((s) => !s)} data-testid="dispatch-panel-toggle">
            {showPanel ? "Hide" : "Show"} review
          </Button>
          <Button
            variant="outline"
            onClick={() => setEmailOpen(true)}
            data-testid="dispatch-email-btn"
            className="border-transparent bg-slate-200 text-slate-800 hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-100 dark:hover:bg-slate-600"
            title="Send daily lineout emails (mocked) — overrides the auto-cron"
          >
            <Mail className="w-3.5 h-3.5 mr-1" /> Email Dispatch
          </Button>
        </div>
      </div>
      <div className="mb-3 flex flex-wrap items-center gap-3" data-testid="dispatch-view-controls">
        <Segmented value={prefs.dispatch_view} onChange={(v) => savePrefs({ dispatch_view: v })}
                   options={[{ key: "board", label: "Board", testId: "dispatch-view-board" }, { key: "calendar", label: "Calendar", testId: "dispatch-view-calendar" }, { key: "linear", label: "Linear 3-week", testId: "dispatch-view-linear" }]} />
        {prefs.dispatch_view !== "calendar" && (
          <Segmented value={prefs.dispatch_grouping} onChange={(v) => savePrefs({ dispatch_grouping: v })}
                     options={[{ key: "crew", label: "By Crew", testId: "dispatch-group-crew" }, { key: "scope", label: "By Scope", testId: "dispatch-group-scope" }, { key: "skills", label: "By Skills", testId: "dispatch-group-skills" }]} />
        )}
        {!prefs.can_write && <span className="text-[11px] text-muted-foreground" data-testid="dispatch-readonly-note" title="Dispatch changes need the Scheduler permission">read-only — Scheduler permission needed to dispatch</span>}
      </div>
      {prefs.dispatch_view === "calendar" ? <ScheduleCalendar /> : prefs.dispatch_view === "linear" ? <DispatchLinear grouping={prefs.dispatch_grouping} /> : (
        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_auto] gap-3">
          <ScheduleBoard key={refreshToken} />
          <DispatchPoolPanel grouping={prefs.dispatch_grouping} expanded={prefs.expanded || {}}
                             onToggleGroup={(k, open) => savePrefs({ expanded: { ...(prefs.expanded || {}), [k]: open } })}
                             reloadKey={poolKey}
                             onChanged={() => { setRefreshToken((t) => t + 1); api.get("/dispatch/quick-requests").then((r) => setRequests((r.data || []).length)).catch(() => {}); }} />
        </div>
      )}
      {showPanel && (
        <div className="mt-4 border-t border-gray-200 pt-4">
          <DispatchPanel projectId={null} />
        </div>
      )}
      <EmailDispatchDialog open={emailOpen} onClose={() => setEmailOpen(false)} />

      {preview && (
        <AutoDispatchPreviewModal
          preview={preview}
          gaps={gaps}
          onClose={() => setPreview(null)}
          onCommit={commit}
          committing={committing}
        />
      )}
    </div>
  );
}

function AutoDispatchPreviewModal({ preview, gaps = [], onClose, onCommit, committing }) {
  const { plan = [], review = [], planned, needs_review } = preview;
  const gapByMob = Object.fromEntries(gaps.map((g) => [g.mobilization_id, g]));
  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      data-testid="auto-dispatch-preview-modal"
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[85vh] flex flex-col overflow-hidden">
        <header className="flex items-center gap-3 px-5 py-3 border-b border-gray-200 bg-gray-50/60">
          <Zap className="w-4 h-4 text-purple-600" />
          <h2 className="text-base font-semibold text-gray-900 flex-1">Auto Dispatch Preview</h2>
          <div className="flex items-center gap-3 text-xs text-gray-600">
            <span className="inline-flex items-center gap-1 text-emerald-700"><Check className="w-3.5 h-3.5" /> {planned} to dispatch</span>
            {gaps.length > 0 && (
              <span className="inline-flex items-center gap-1 text-red-700" data-testid="dispatch-gap-count">
                <AlertTriangle className="w-3.5 h-3.5" /> {gaps.length} short of material
              </span>
            )}
            {needs_review > 0 && (
              <span className="inline-flex items-center gap-1 text-amber-700"><AlertTriangle className="w-3.5 h-3.5" /> {needs_review} need review</span>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700"
            data-testid="auto-dispatch-close-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto">
          {plan.length > 0 && (
            <section className="px-5 py-4 border-b border-gray-100">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-600 mb-2">Assignments</h3>
              <div className="space-y-1.5">
                {plan.map((row) => (
                  <div
                    key={row.mobilization_id}
                    className="border border-gray-200 rounded-md px-3 py-2 bg-white hover:bg-blue-50/30 text-sm"
                    data-testid={`auto-dispatch-plan-row-${row.mobilization_id}`}
                  >
                    <div className="flex items-baseline justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-gray-900 truncate">
                          {row.project_name || row.title}
                          <span className="ml-2 text-xs font-normal text-gray-500">{row.scope_type || "—"}</span>
                        </div>
                        <div className="text-xs text-gray-500">
                          {row.date} · {row.duration_hours}h · region {row.region}
                        </div>
                      </div>
                      <div className="shrink-0 text-right">
                        <div className="text-xs font-semibold text-gray-900 flex items-center justify-end gap-1">
                          <Users className="w-3 h-3" />
                          {row.crew_members.map((m) => m.name).join(", ")}
                        </div>
                        <div className="text-[10px] text-purple-700 uppercase tracking-wide">
                          Lead: {row.primary_installer_name}
                        </div>
                      </div>
                    </div>
                    {gapByMob[row.mobilization_id] && (
                      <div className="mt-1.5 rounded border border-red-200 bg-red-50 px-2 py-1.5 text-[11px] text-red-900"
                           data-testid={`dispatch-gap-${row.mobilization_id}`}>
                        <div className="font-semibold flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" />
                          {gapByMob[row.mobilization_id].rooms} room(s) have material that is not on
                          hand or not ordered
                        </div>
                        <ul className="mt-0.5 ml-4 list-disc">
                          {gapByMob[row.mobilization_id].lines.slice(0, 6).map((l, i) => (
                            <li key={i}>
                              {l.room} · {l.product} — {l.not_on_hand > 0
                                ? `${l.not_on_hand} not on hand` : `${l.not_ordered} not ordered`}
                              {l.eta ? ` (ETA ${l.eta})` : ""}
                            </li>
                          ))}
                        </ul>
                        {gapByMob[row.mobilization_id].lines.length > 6 && (
                          <div className="ml-4">
                            +{gapByMob[row.mobilization_id].lines.length - 6} more
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {review.length > 0 && (
            <section className="px-5 py-4 bg-amber-50/40">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-amber-800 mb-2 flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5" /> Needs Review
              </h3>
              <div className="space-y-1.5">
                {review.map((r) => (
                  <div
                    key={r.mobilization_id}
                    className="border border-amber-200 rounded-md px-3 py-2 bg-white text-sm"
                    data-testid={`auto-dispatch-review-row-${r.mobilization_id}`}
                  >
                    <div className="font-semibold text-gray-900 truncate">
                      {r.project_name || r.title}
                    </div>
                    <div className="text-xs text-amber-800">{r.reason}</div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {plan.length === 0 && review.length === 0 && (
            <div className="px-5 py-12 text-center text-gray-500 text-sm">
              No mobilizations to auto-dispatch. Send mobs from the Schedule board or wait for the 14-day auto-flip.
            </div>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-200 bg-gray-50/60">
          <Button variant="outline" onClick={onClose} disabled={committing} data-testid="auto-dispatch-cancel-btn">
            Cancel
          </Button>
          <Button
            onClick={onCommit}
            disabled={committing || plan.length === 0}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="auto-dispatch-commit-btn"
          >
            <Zap className="w-3.5 h-3.5 mr-1" />
            {committing ? "Committing…" : `Commit ${plan.length} assignments`}
          </Button>
        </footer>
      </div>
    </div>
  );
}
